import { useEffect, useMemo, useState } from "react";
import {
  EnvelopeSimple,
  EnvelopeOpen,
  Trash,
  CircleNotch,
  Phone,
  User,
  MapPin,
  CalendarBlank,
  X,
} from "@phosphor-icons/react";
import {
  listSubmissions,
  markSubmissionRead,
  deleteSubmission,
} from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterUnread, setFilterUnread] = useState(false);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    listSubmissions({ unread: filterUnread })
      .then((r) => setItems(r.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUnread]);

  const openDetail = async (s) => {
    setDetail(s);
    if (!s.is_read) {
      try {
        await markSubmissionRead(s.id, true);
        setItems((prev) =>
          prev.map((x) => (x.id === s.id ? { ...x, is_read: true } : x))
        );
      } catch {
        // silent — la UI mostrerà comunque il dettaglio
      }
    }
  };

  const toggleRead = async (s) => {
    setBusyId(s.id);
    try {
      await markSubmissionRead(s.id, !s.is_read);
      setItems((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, is_read: !s.is_read } : x))
      );
      if (detail?.id === s.id) setDetail({ ...detail, is_read: !s.is_read });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteSubmission(deleteTarget.id);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      if (detail?.id === deleteTarget.id) setDetail(null);
      setDeleteTarget(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const unreadCount = useMemo(
    () => items.filter((s) => !s.is_read).length,
    [items]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Richieste dal sito</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-medium">
            {items.length}{" "}
            <span className="text-[#5C5A55] font-light">
              {items.length === 1 ? "messaggio" : "messaggi"}
            </span>
            {unreadCount > 0 && (
              <span className="ml-3 inline-flex items-center px-2.5 py-1 bg-[#EB0D0D] text-[#FAF9F6] text-xs uppercase tracking-[0.15em] font-bold align-middle">
                {unreadCount} da leggere
              </span>
            )}
          </h2>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setFilterUnread(false)}
            data-testid="filter-all"
            className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border transition-colors ${
              !filterUnread
                ? "bg-[#1A1918] text-[#FAF9F6] border-[#1A1918]"
                : "bg-transparent text-[#1A1918] border-[#D1CFC7] hover:border-[#1A1918]"
            }`}
          >
            Tutte
          </button>
          <button
            onClick={() => setFilterUnread(true)}
            data-testid="filter-unread"
            className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border transition-colors ${
              filterUnread
                ? "bg-[#EB0D0D] text-[#FAF9F6] border-[#EB0D0D]"
                : "bg-transparent text-[#1A1918] border-[#D1CFC7] hover:border-[#EB0D0D]"
            }`}
          >
            Solo non lette
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <CircleNotch
            size={28}
            weight="bold"
            className="animate-spin text-[#EB0D0D]"
          />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#F0EFEB] border border-[#D1CFC7]/40 py-16 text-center text-[#5C5A55]">
          Nessuna richiesta con questi filtri.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-submissions-list">
          {items.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => openDetail(s)}
              data-testid={`submission-item-${s.id}`}
              className={`w-full text-left bg-[#F0EFEB] hover:bg-[#E2E0D8] transition-colors p-5 border border-[#D1CFC7]/40 grid grid-cols-1 md:grid-cols-12 gap-4 items-center ${
                !s.is_read ? "border-l-4 border-l-[#EB0D0D]" : ""
              }`}
            >
              <div className="md:col-span-3 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5C5A55]">
                    {s.tipo === "preventivo" ? "Preventivo" : "Contatto"}
                  </span>
                  {!s.is_read && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#EB0D0D]" />
                  )}
                </div>
                <div className="font-display text-lg truncate">{s.nome}</div>
                <div className="text-xs text-[#5C5A55] truncate">{s.email}</div>
              </div>

              <div className="md:col-span-6 min-w-0">
                <p className="text-sm text-[#1A1918] line-clamp-2">
                  {s.messaggio}
                </p>
                {(s.telefono || s.citta) && (
                  <div className="flex gap-3 mt-1.5 text-xs text-[#5C5A55]">
                    {s.telefono && (
                      <span className="inline-flex items-center gap-1">
                        <Phone size={12} weight="bold" />
                        {s.telefono}
                      </span>
                    )}
                    {s.citta && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} weight="bold" />
                        {s.citta}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="md:col-span-2 text-xs text-[#5C5A55] whitespace-nowrap">
                {formatDate(s.created_at)}
              </div>

              <div
                className="md:col-span-1 flex md:justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRead(s);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleRead(s);
                    }
                  }}
                  className="p-2 hover:bg-[#FAF9F6] text-[#1A1918] cursor-pointer"
                  title={s.is_read ? "Segna come non letta" : "Segna come letta"}
                  data-testid={`toggle-read-${s.id}`}
                >
                  {s.is_read ? (
                    <EnvelopeSimple size={16} weight="regular" />
                  ) : (
                    <EnvelopeOpen size={16} weight="regular" />
                  )}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(s);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDeleteTarget(s);
                    }
                  }}
                  className="p-2 hover:bg-[#EB0D0D]/10 text-[#EB0D0D] cursor-pointer"
                  title="Elimina"
                  data-testid={`delete-${s.id}`}
                >
                  <Trash size={16} weight="regular" />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
          onClick={() => setDetail(null)}
        >
          <div
            className="bg-[#FAF9F6] w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-[#D1CFC7] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="submission-detail-modal"
          >
            <div className="flex items-start justify-between p-6 md:p-8 border-b border-[#D1CFC7]/60">
              <div>
                <div className="overline mb-2">
                  {detail.tipo === "preventivo" ? "Preventivo" : "Contatto"}
                </div>
                <h3 className="font-display text-3xl tracking-tighter font-medium">
                  {detail.nome}
                </h3>
              </div>
              <button
                onClick={() => setDetail(null)}
                className="p-2 hover:bg-[#F0EFEB] transition-colors"
                aria-label="Chiudi"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            <div className="p-6 md:p-8 space-y-5">
              <InfoRow icon={User} label="Nome" value={detail.nome} />
              <InfoRow
                icon={EnvelopeSimple}
                label="Email"
                value={
                  <a
                    href={`mailto:${detail.email}`}
                    className="text-[#EB0D0D] hover:underline break-all"
                  >
                    {detail.email}
                  </a>
                }
              />
              {detail.telefono && (
                <InfoRow
                  icon={Phone}
                  label="Telefono"
                  value={
                    <a
                      href={`tel:${detail.telefono}`}
                      className="text-[#EB0D0D] hover:underline"
                    >
                      {detail.telefono}
                    </a>
                  }
                />
              )}
              {detail.citta && (
                <InfoRow icon={MapPin} label="Città" value={detail.citta} />
              )}
              <InfoRow
                icon={CalendarBlank}
                label="Ricevuto"
                value={formatDate(detail.created_at)}
              />

              <div>
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#5C5A55] mb-2">
                  Messaggio
                </div>
                <div className="p-4 bg-[#F0EFEB] border-l-2 border-[#EB0D0D] text-[#1A1918] whitespace-pre-wrap text-sm leading-relaxed">
                  {detail.messaggio}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-between items-center gap-3 p-6 md:p-8 border-t border-[#D1CFC7]/60 bg-[#F0EFEB]">
              <a
                href={`mailto:${detail.email}?subject=Re:%20Koreko%20Direct`}
                className="inline-flex items-center gap-2 text-[#EB0D0D] font-bold text-xs uppercase tracking-[0.15em] hover:underline"
              >
                <EnvelopeSimple size={16} weight="bold" />
                Rispondi via email
              </a>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleRead(detail)}
                  disabled={busyId === detail.id}
                  className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
                >
                  {detail.is_read ? "Segna non letta" : "Segna letta"}
                </button>
                <button
                  onClick={() => setDeleteTarget(detail)}
                  className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#EB0D0D] border border-[#EB0D0D] hover:bg-[#EB0D0D] hover:text-[#FAF9F6] transition-colors inline-flex items-center gap-2"
                >
                  <Trash size={14} weight="bold" />
                  Elimina
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
          onClick={() => (busyId ? null : setDeleteTarget(null))}
        >
          <div
            className="bg-[#FAF9F6] w-full max-w-md border border-[#D1CFC7] shadow-2xl p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <div className="overline mb-3">Conferma eliminazione</div>
            <h3 className="font-display text-2xl tracking-tighter font-medium mb-3">
              Eliminare la richiesta di{" "}
              <em className="italic font-light text-[#EB0D0D]">
                {deleteTarget.nome}
              </em>
              ?
            </h3>
            <p className="text-sm text-[#5C5A55] mb-6">
              L'operazione è irreversibile. La richiesta verrà rimossa
              definitivamente dal database.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={!!busyId}
                className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={confirmDelete}
                disabled={!!busyId}
                data-testid="delete-confirm-btn"
                className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] hover:bg-[#C00A0A] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busyId ? (
                  <CircleNotch size={14} weight="bold" className="animate-spin" />
                ) : (
                  <Trash size={14} weight="bold" />
                )}
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <Icon
          size={16}
          weight="regular"
          className="mt-1 text-[#5C5A55] flex-shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5C5A55]">
          {label}
        </div>
        <div className="text-sm text-[#1A1918]">{value}</div>
      </div>
    </div>
  );
}
